/* The relationship graph.
 *
 * Flat CS tools model Account → Contact and stop. Real customers are graphs:
 *   Organization ──< Contract ──< Instance ──< User ──> Person
 *   Organization ──< Organization         (corporate / subsidiary edges)
 *   Person ──< Affiliation >── Organization | Instance   (context-scoped roles)
 *
 * "Instance" is the unit of delivery — a deployment — and it carries the
 * usage/health. A Person is a human identity; a User is a login on one
 * Instance, so one Person can own many Users. Affiliations are the
 * many-to-many edges that answer "who is this a contact for" with *both* sides
 * rather than one, because that question rarely has a single answer.
 *
 * The graph is DERIVED from the live workspace — see graphFromWorkspace.ts —
 * so Relationships is a lens on the same accounts the rest of the app shows,
 * not a separate dataset. This file owns the model + the rollup/resolution
 * selectors that operate on any GraphModel. */

export interface OrgNode {
  id: string;              // === the account id when this org is an account
  name: string;
  type: string;            // segment label, or "Parent company"
  parentOrgId?: string;
  color: string;
  accountId?: string;      // set when this org maps to a workspace account (cross-link)
}

export interface Contract {
  id: string;
  orgId: string; // the paying party
  name: string;
  revenueType: "subscription" | "services";
  amount: number;
  amountLabel: string; // "ARR" | "Contract value"
  startDate: string;
  endDate: string;
  status: "active" | "renewing" | "at risk";
}

export interface Instance {
  id: string;
  contractId: string;
  name: string;
  health: number;
  metrics: { label: string; value: string }[]; // instance-specific stats
  accountId?: string;      // the workspace account this instance belongs to (cross-link)
}

export interface Person {
  id: string;
  name: string;
  title: string;
  email: string;
  employerOrgId: string;
}

export interface UserAccount {
  id: string;
  personId: string;
  instanceId: string;
  role: string;
  lastActive: string; // ISO date
  active: boolean;
}

export interface Affiliation {
  id: string;
  personId: string;
  targetType: "org" | "instance";
  targetId: string;
  role: string;
  note?: string; // context, e.g. "subcontracted to the general contractor"
}

export interface GraphModel {
  vocab: { org: string; contract: string; instance: string; instancePlural: string; revenue: string };
  orgs: OrgNode[];
  contracts: Contract[];
  instances: Instance[];
  people: Person[];
  users: UserAccount[];
  affiliations: Affiliation[];
}


/* --------------------------- selectors / rollups --------------------------- */

export const bandOf = (h: number): "good" | "watch" | "risk" => (h >= 70 ? "good" : h >= 45 ? "watch" : "risk");

export const contractsForOrg = (g: GraphModel, orgId: string) => g.contracts.filter((c) => c.orgId === orgId);
export const instancesForContract = (g: GraphModel, contractId: string) => g.instances.filter((i) => i.contractId === contractId);
export const instancesForOrg = (g: GraphModel, orgId: string) =>
  contractsForOrg(g, orgId).flatMap((c) => instancesForContract(g, c.id));
export const childOrgs = (g: GraphModel, orgId: string) => g.orgs.filter((o) => o.parentOrgId === orgId);
export const usersForInstance = (g: GraphModel, instanceId: string) => g.users.filter((u) => u.instanceId === instanceId);
export const usersForPerson = (g: GraphModel, personId: string) => g.users.filter((u) => u.personId === personId);
export const orgById = (g: GraphModel, id: string) => g.orgs.find((o) => o.id === id);
export const instanceById = (g: GraphModel, id: string) => g.instances.find((i) => i.id === id);
export const personById = (g: GraphModel, id: string) => g.people.find((p) => p.id === id);
export const contractById = (g: GraphModel, id: string) => g.contracts.find((c) => c.id === id);

export interface Rollup {
  revenue: number;
  revenueLabel: string;
  contracts: number;
  instances: number;
  health: number; // avg across instances
  people: number; // distinct people with a login below this node
  activeUsers: number; // distinct active people
}

function rollupFromInstances(g: GraphModel, instances: Instance[], contracts: Contract[]): Rollup {
  const instanceIds = new Set(instances.map((i) => i.id));
  const users = g.users.filter((u) => instanceIds.has(u.instanceId));
  const people = new Set(users.map((u) => u.personId));
  const active = new Set(users.filter((u) => u.active).map((u) => u.personId));
  const health = instances.length ? Math.round(instances.reduce((s, i) => s + i.health, 0) / instances.length) : 0;
  const revenue = contracts.reduce((s, c) => s + c.amount, 0);
  return {
    revenue,
    revenueLabel: g.vocab.revenue,
    contracts: contracts.length,
    instances: instances.length,
    health,
    people: people.size,
    activeUsers: active.size,
  };
}

/** Roll an org up including any child orgs (corporate hierarchy). */
export function rollupOrg(g: GraphModel, orgId: string): Rollup {
  const orgIds = [orgId, ...childOrgs(g, orgId).map((o) => o.id)];
  const contracts = g.contracts.filter((c) => orgIds.includes(c.orgId));
  const instances = contracts.flatMap((c) => instancesForContract(g, c.id));
  return rollupFromInstances(g, instances, contracts);
}

export function rollupContract(g: GraphModel, contractId: string): Rollup {
  const c = contractById(g, contractId);
  const instances = instancesForContract(g, contractId);
  return rollupFromInstances(g, instances, c ? [c] : []);
}

/** Identity resolution: everything a Person touches, across all their logins. */
export interface PersonResolution {
  person: Person;
  employer?: OrgNode;
  users: (UserAccount & { instance?: Instance; org?: OrgNode })[];
  instanceCount: number;
  activeUserCount: number;
  /** Customer orgs this person appears under (via their project/account logins). */
  appearsUnder: { org: OrgNode; via: string }[];
  affiliations: (Affiliation & { targetName: string; targetKind: string })[];
}

export function resolvePerson(g: GraphModel, personId: string): PersonResolution | null {
  const person = personById(g, personId);
  if (!person) return null;
  const users = usersForPerson(g, personId).map((u) => {
    const instance = instanceById(g, u.instanceId);
    const contract = instance ? contractById(g, instance.contractId) : undefined;
    const org = contract ? orgById(g, contract.orgId) : undefined;
    return { ...u, instance, org };
  });

  // customer orgs this person shows up under, via the instances they can access
  const appears = new Map<string, { org: OrgNode; via: string }>();
  for (const u of users) {
    if (u.org && !appears.has(u.org.id)) {
      appears.set(u.org.id, { org: u.org, via: `${u.role} on ${u.org.name} ${g.vocab.instancePlural.toLowerCase()}` });
    }
  }
  // plus their employer, if different from where they appear
  const employer = orgById(g, person.employerOrgId);
  if (employer && !appears.has(employer.id)) {
    appears.set(employer.id, { org: employer, via: "Employer" });
  }

  const affiliations = g.affiliations
    .filter((a) => a.personId === personId)
    .map((a) => {
      const targetName = a.targetType === "org" ? orgById(g, a.targetId)?.name ?? a.targetId : instanceById(g, a.targetId)?.name ?? a.targetId;
      return { ...a, targetName, targetKind: a.targetType === "org" ? "Organization" : g.vocab.instance };
    });

  return {
    person,
    employer,
    users,
    instanceCount: new Set(users.map((u) => u.instanceId)).size,
    activeUserCount: users.filter((u) => u.active).length,
    appearsUnder: [...appears.values()],
    affiliations,
  };
}
